/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-refresh/only-export-components */
import { useCallback, useEffect, useContext, memo, useMemo } from 'react';
import { useState } from 'react';
import styled from '@emotion/styled';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import { Checkbox } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';
import type { Cell } from 'react-table';
import { useInvalidateFilesQueries } from '@/api/hooks/useInvalidateFileQueries';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context.js';
import { WorkspaceObjectType } from '@/enums';
import { FilesTablePageDataContext, SelectedObjectsContext } from './context';
import { NameCell } from './Files.Table.Cells.Name';
/* import { ActionsCell } from './Files.Table.Cells.Actions'; */
import { PublishedStatusCell } from './Files.Table.Cells.PublishedStatus';
import { TagsCell } from './Files.Table.Cells.Tags';
import type { IFilesTable } from './interfaces';
import styles from './style/Files.Table.Cells.module.css';
import { distinct } from '@trinity/chat-components/utils/array.js';

const OwnerCell = memo(({ row: { original: data } }: Cell<IFilesTable.Item>) => {
  return (
    <>{data.meta.owner.displayName}</>
  );
});

const CreatedOnCell = memo((props: Cell<IFilesTable.Item>) => {
  if (props.row.original.object.typeId === WorkspaceObjectType.Brand) {
    return <>-</>;
  }

  if (props.row.original.object.typeId === WorkspaceObjectType.ProjectParent) {
    return <>{format(new Date(props.row.original.object.createdOn), 'MM/dd/yyyy')}</>;
  }

  if ('file' in props.row.original && props.row.original.file.publishedOn) {
    return <>{format(new Date(props.row.original.file.publishedOn), 'MM/dd/yyyy')}</>;
  }

  return (
    <>{format(new Date(props.row.original.object.createdOn), 'MM/dd/yyyy')}</>
  );
});

function BookmarkCell({ row: { original: data } }: Cell<IFilesTable.Item>) {
  const invalidateFilesQueries = useInvalidateFilesQueries();

  const apiClient = useChatApiClient();

  const { mutateAsync, isPending } = useMutation({
    mutationKey: ['file.bookmarkObject', data.object.id],
    mutationFn: async () => {
      return apiClient.workspacesObject.toggleBookmark({
        objectId: data.object.id,
        workspaceId: data.object.workspaceId,
      });
    }, onSuccess: res => {
      invalidateFilesQueries();
    },
  });
  const toggleBookmark = useCallback(() => {
    mutateAsync();
  }, [mutateAsync]);

  const { palette, theme: wl } = useContext(AppThemingContext);
  const fill = wl?.enabled ? palette.primary.dark : `#2596be`;
  const color = wl?.enabled ? palette.primary.light : `#737373`;

  const disabled = isPending;
  return (
    <button
      className={disabled ? styles.disabled : ''}
      disabled={disabled}
      onClick={toggleBookmark}>
      {data.bookmarked ? (
        <PushPinIcon
          sx={{
            color: disabled ? `var(--gray-l)` : fill,
            fontSize: 24,
          }}
        />
      ) : (
        <PushPinOutlinedIcon
          sx={{
            color: disabled ? `var(--gray-l)` : color,
            fontSize: 24,
          }}
        />
      )}
    </button>
  );

}

function CheckboxCell({ row: { original: data } }: Cell<IFilesTable.Item>) {
  const { selectedObjectIds, toggleSelectedObject } = useContext(SelectedObjectsContext);
  const { palette, enabled } = useContext(AppThemingContext);

  const checkedColor = enabled && palette?.primary?.main
    ? palette.primary.main
    : 'var(--pri-02)';

  return (
    <StyledCheckbox
      checked={selectedObjectIds.includes(data.object.id)}
      onChange={() => toggleSelectedObject(data.object.id)}
      sx={{
        color: 'var(--gray-d)',
        '&.Mui-checked': {
          color: checkedColor,
        },
      }} />
  );
}

function CheckboxHeader() {
  const { selectedObjectIds, setSelectedObjects } = useContext(SelectedObjectsContext);
  const data = useContext(FilesTablePageDataContext);
  const { palette, enabled } = useContext(AppThemingContext);
  const count = selectedObjectIds.length;

  const checkedColor = enabled && palette?.primary
    ? palette.primary.main
    : 'var(--pri-02)';

  const handleClick = useCallback(() => {
    setSelectedObjects(oldSelectedObjectIds => {
      const selectedPageObjects = oldSelectedObjectIds.filter(objectId => data.some(x => x.object.id === objectId));

      if (selectedPageObjects.length > 0) {
        const targetIds = oldSelectedObjectIds.filter(objectId => !selectedPageObjects.includes(objectId));
        return targetIds;
      }
      else {
        const selectedIds = data.map(row => row.object.id);
        return distinct(selectedIds.concat(oldSelectedObjectIds));
      }
    });
  }, [data, setSelectedObjects]);

  const filterCount = useMemo(() => selectedObjectIds.filter(objectId => data.some(x => x.object.id === objectId)).length, [data, selectedObjectIds]);

  return (
    <div className={styles.root}>
      <StyledCheckbox
        onChange={handleClick}
        checked={filterCount > 0}
        indeterminate={filterCount < data.length && filterCount > 0}
        sx={{
          color: 'var(--gray-d)',
          '&.Mui-checked': {
            color: checkedColor,
          },
          '&.MuiCheckbox-indeterminate': {
            color: checkedColor,
          },
        }}>
      </StyledCheckbox>
      {count > 0 && <div className={styles.checkboxLabel}>({count})</div>}
    </div>
  );
}

export default {
  Actions: () => null, // ActionsCell,
  Checkbox: CheckboxCell,
  CheckboxHeader: CheckboxHeader,
  CreatedOn: CreatedOnCell,
  Name: NameCell,
  Owner: OwnerCell,
  Bookmark: BookmarkCell,
  PublishedStatus: PublishedStatusCell,
  Tags: TagsCell,
};

const StyledCheckbox = styled(Checkbox)({
  padding: 0,
});